import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface UnsavedProfileChangesDialogProps {
  isOpen: boolean;
  isSaving?: boolean;
  /** Close the prompt and stay on the form. */
  onCancel: () => void;
  /** Drop unsaved edits and continue leaving. */
  onDiscard: () => void;
  onSave: () => void;
}

const UnsavedProfileChangesDialog: React.FC<UnsavedProfileChangesDialogProps> = ({
  isOpen,
  isSaving = false,
  onCancel,
  onDiscard,
  onSave,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving) onCancel();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onCancel]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-sm"
      onClick={event => {
        if (event.target === event.currentTarget && !isSaving) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-profile-changes-title"
    >
      <div
        className="w-[320px] max-w-full rounded-2xl border border-gray-700 px-6 pb-6 pt-7 shadow-xl"
        style={{ backgroundColor: '#1E1E1C', fontFamily: 'Rubik, sans-serif' }}
        onClick={event => event.stopPropagation()}
      >
        <h2
          id="unsaved-profile-changes-title"
          className="text-center text-base font-medium text-white"
        >
          Save your changes?
        </h2>
        <div className="mt-8 flex items-center justify-between px-1">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isSaving}
            className="text-sm text-white underline disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="profile-save-rainbow relative rounded-lg text-sm font-medium text-white transition-transform duration-200 hover:scale-105 active:scale-110 disabled:opacity-80 disabled:hover:scale-100"
          >
            <span className="flex min-w-[5.5rem] items-center justify-center gap-2 rounded-lg bg-dark-950 px-3 py-1.5">
              {isSaving ? (
                <>
                  <span
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                    aria-hidden
                  />
                  Saving…
                </>
              ) : (
                'SAVE'
              )}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UnsavedProfileChangesDialog;
